import React from 'react';
import { Smartphone, DollarSign, Shield, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const DigitalBanks: React.FC = () => {
  useSEO({
    title: 'Melhores Bancos Digitais 2026: Comparativo Nubank, Inter, C6 e PicPay | O Teu Dinheiro',
    description: 'Qual o melhor banco digital em 2026? Comparamos rendimento em conta (CDI), tarifas, cartões sem anuidade e segurança dos principais bancos digitais.',
    keywords: 'melhores bancos digitais 2026, Nubank vs Inter, C6 Bank, conta digital que rende 100 do CDI, banco digital gratuito',
    url: 'https://www.oteudinheiro.com.br/bancos-digitais'
  });
  const banks = [
    {
      name: 'Nubank',
      logo: '🟣',
      description: 'Cartão roxo sem anuidade e conta digital completa',
      pros: ['Sem anuidade', 'App intuitivo', 'Cartão virtual', 'Suporte 24h'],
      cons: ['Limite inicial baixo', 'Sem agências físicas'],
      rating: 4.8
    },
    {
      name: 'Inter',
      logo: '🟠',
      description: 'Banco completo com investimentos e cartão sem anuidade',
      pros: ['Banco completo', 'Investimentos', 'Cartão sem anuidade', 'Agências físicas'],
      cons: ['App pode ser complexo', 'Taxas em alguns serviços'],
      rating: 4.6
    },
    {
      name: 'C6 Bank',
      logo: '⚫',
      description: 'Cartão com cashback e conta digital avançada',
      pros: ['Cashback', 'Cartão sem anuidade', 'Investimentos', 'Suporte rápido'],
      cons: ['Limite pode ser baixo', 'Alguns bugs no app'],
      rating: 4.4
    },
    {
      name: 'PicPay',
      logo: '🟡',
      description: 'Carteira digital com cartão de crédito',
      pros: ['Carteira digital', 'Cartão sem anuidade', 'PIX instantâneo', 'Cashback'],
      cons: ['Não é banco completo', 'Limite de saque'],
      rating: 4.2
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20">
        <div className="container-custom text-center">
          <Smartphone className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Bancos Digitais
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Conheça as melhores opções de bancos digitais do Brasil
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que são bancos digitais?
              </h2>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  Bancos digitais são instituições financeiras que operam principalmente
                  através de aplicativos e internet, sem a necessidade de agências físicas.
                </p>
                <p className="mb-4">
                  Eles oferecem serviços bancários tradicionais com taxas menores,
                  cartões sem anuidade e uma experiência mais moderna e ágil.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Vantagens dos bancos digitais
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Taxas menores</h4>
                    <p className="text-gray-600 text-sm">Sem anuidade de cartão e taxas reduzidas</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Smartphone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">App intuitivo</h4>
                    <p className="text-gray-600 text-sm">Interface moderna e fácil de usar</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Investimentos</h4>
                    <p className="text-gray-600 text-sm">Acesso a CDBs, fundos e outros produtos</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Segurança</h4>
                    <p className="text-gray-600 text-sm">Mesma proteção dos bancos tradicionais</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Quando escolher um banco digital?
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Quer economizar</h4>
                      <p className="text-gray-600 text-sm">Sem anuidade e taxas menores</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Usa muito o celular</h4>
                      <p className="text-gray-600 text-sm">App completo e funcional</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Quer investir</h4>
                      <p className="text-gray-600 text-sm">Produtos de investimento integrados</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Precisa de agência</h4>
                      <p className="text-gray-600 text-sm">Maioria não tem agências físicas</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Serviços complexos</h4>
                      <p className="text-gray-600 text-sm">Alguns serviços podem ser limitados</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banks Comparison */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Comparativo dos principais bancos digitais
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {banks.map((bank) => (
              <div key={bank.name} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{bank.logo}</span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{bank.name}</h3>
                      <div className="flex items-center space-x-1">
                        <span className="text-yellow-500">★</span>
                        <span className="text-sm text-gray-600">{bank.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">{bank.description}</p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-700 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Vantagens
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {bank.pros.map((pro, index) => (
                        <li key={index} className="flex items-center">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                      <XCircle className="w-4 h-4 mr-1" />
                      Desvantagens
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {bank.cons.map((con, index) => (
                        <li key={index} className="flex items-center">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como escolher o melhor banco digital
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Analise suas necessidades
              </h3>
              <p className="text-gray-600">
                Defina o que é mais importante: cartão sem anuidade, investimentos,
                ou apenas uma conta digital.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Compare as opções
              </h3>
              <p className="text-gray-600">
                Veja as taxas, limites, funcionalidades e avaliações de cada banco
                antes de decidir.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Teste o app
              </h3>
              <p className="text-gray-600">
                Baixe o app e teste a interface antes de abrir a conta.
                A usabilidade é fundamental.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas importantes
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Comece com um
              </h3>
              <p className="text-secondary-700">
                Não abra conta em vários bancos digitais de uma vez.
                Comece com um e veja se atende suas necessidades.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Mantenha um banco tradicional
              </h3>
              <p className="text-accent-700">
                Para serviços mais complexos ou emergências, é bom manter
                uma conta em banco tradicional.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Use o cartão com responsabilidade
              </h3>
              <p className="text-secondary-700">
                Mesmo sem anuidade, controle seus gastos para não acumular
                dívidas no cartão de crédito.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Verifique a segurança
              </h3>
              <p className="text-accent-700">
                Certifique-se de que o banco digital é regulamentado pelo
                Banco Central e tem proteção ao investidor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-blue-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Escolha o banco digital ideal para você!
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Economize com taxas menores e aproveite uma experiência bancária mais moderna.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/cartao-credito"
              className="btn bg-white text-blue-600 hover:bg-gray-100"
            >
              Ver cartões de crédito
            </a>
            <a
              href="/pix"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-blue-600"
            >
              Entender PIX
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DigitalBanks;
